import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Zap } from "lucide-react";
import { navLinks } from "../../data/content";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (href) => {
    setOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-[background,box-shadow,padding] duration-300 ${
        scrolled ? "glass shadow-[0_8px_30px_rgba(0,51,255,0.06)] py-3" : "py-5"
      }`}
      data-testid="site-navbar"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
        <button
          onClick={() => scrollTo("#home")}
          className="flex items-center gap-2 group"
          data-testid="nav-logo"
        >
          <span className="w-9 h-9 rounded-xl bg-[#0033FF] flex items-center justify-center text-white shadow-[0_6px_18px_rgba(0,51,255,0.35)] group-hover:rotate-12 transition-transform duration-300">
            <Zap className="w-5 h-5" fill="currentColor" />
          </span>
          <span className="font-display font-extrabold text-xl tracking-tight text-[#0b1221]">
            QuickDrop<span className="text-[#0033FF]"> Go</span>
          </span>
        </button>

        <nav className="hidden md:flex items-center gap-1">
          {navLinks.map((l) => (
            <button
              key={l.href}
              onClick={() => scrollTo(l.href)}
              className="px-4 py-2 text-sm font-medium text-[#5e6c84] hover:text-[#0033FF] transition-colors duration-200"
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
            >
              {l.label}
            </button>
          ))}
        </nav>

        <button
          onClick={() => scrollTo("#download")}
          className="hidden md:inline-flex items-center gap-2 bg-[#0033FF] text-white text-sm font-semibold px-5 py-2.5 rounded-full hover:bg-[#0026bf] hover:scale-[1.03] active:scale-95 transition-[transform,background-color] duration-200 shadow-[0_8px_24px_rgba(0,51,255,0.28)]"
          data-testid="nav-download-btn"
        >
          Download App
        </button>

        <button
          className="md:hidden p-2 text-[#0b1221]"
          onClick={() => setOpen((o) => !o)}
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden glass mt-3 mx-4 rounded-2xl"
            data-testid="nav-mobile-menu"
          >
            <div className="flex flex-col p-4 gap-1">
              {navLinks.map((l) => (
                <button
                  key={l.href}
                  onClick={() => scrollTo(l.href)}
                  className="text-left px-4 py-3 rounded-xl text-[#0b1221] font-medium hover:bg-[#0033FF]/5"
                >
                  {l.label}
                </button>
              ))}
              <button
                onClick={() => scrollTo("#download")}
                className="mt-2 bg-[#0033FF] text-white font-semibold px-4 py-3 rounded-xl"
              >
                Download App
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
